// useWiki.js — thin, auth-bound client for every /api/wiki endpoint.
// Components call e.g. `const api = useWikiApi(); await api.tree(dept)`.
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch, apiPost, apiPut, apiPatch, apiDelete } from '../lib/api';

const qp = (obj) => Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

export function useWikiApi() {
    const { getAuthHeader } = useAuth();
    return useMemo(() => {
        const h = () => getAuthHeader();
        return {
            access: () => apiFetch('/wiki/access', h()),
            seed: () => apiPost('/wiki/seed', {}, h()),
            tree: (dept) => apiFetch(`/wiki/tree?${qp({ dept })}`, h()),
            page: (id) => apiFetch(`/wiki/pages/${id}`, h()),
            createPage: (body) => apiPost('/wiki/pages', body, h()),
            updatePage: (id, body) => apiPatch(`/wiki/pages/${id}`, body, h()),
            saveBlocks: (id, body) => apiPut(`/wiki/pages/${id}/blocks`, body, h()),
            deletePage: (id) => apiDelete(`/wiki/pages/${id}`, h()),
            createFolder: (body) => apiPost('/wiki/folders', body, h()),
            updateFolder: (id, body) => apiPatch(`/wiki/folders/${id}`, body, h()),
            deleteFolder: (id) => apiDelete(`/wiki/folders/${id}`, h()),
            archivePage: (id) => apiPost(`/wiki/pages/${id}/archive`, {}, h()),
            restorePage: (id) => apiPost(`/wiki/pages/${id}/restore`, {}, h()),
            archiveFolder: (id) => apiPost(`/wiki/folders/${id}/archive`, {}, h()),
            restoreFolder: (id) => apiPost(`/wiki/folders/${id}/restore`, {}, h()),
            archived: (dept) => apiFetch(`/wiki/archived?${qp({ dept })}`, h()),
            versions: (id) => apiFetch(`/wiki/pages/${id}/versions`, h()),
            diff: (id, a, b) => apiFetch(`/wiki/pages/${id}/diff?${qp({ a, b })}`, h()),
            confirmRead: (id) => apiPost(`/wiki/pages/${id}/read`, {}, h()),
            readers: (id) => apiFetch(`/wiki/pages/${id}/readers`, h()),
            remind: (id, body) => apiPost(`/wiki/pages/${id}/remind`, body, h()),
            getAcl: (scope, scopeId) => apiFetch(`/wiki/acl?${qp({ scope, scope_id: scopeId })}`, h()),
            setAcl: (body) => apiPut('/wiki/acl', body, h()),
            agentChat: (body) => apiPost('/wiki/agent/chat', body, h()),
            ask: (body) => apiPost('/wiki/ask', body, h()),
            feedback: (id, body) => apiPost(`/wiki/interactions/${id}/feedback`, body, h()),
            adminInteractions: (dept, status) => apiFetch(`/wiki/admin/interactions?${qp({ dept, status })}`, h()),
            adminInteraction: (id) => apiFetch(`/wiki/admin/interactions/${id}`, h()),
            getTraining: () => apiFetch('/wiki/admin/training', h()),
            setTraining: (body) => apiPut('/wiki/admin/training', body, h()),
            retrain: () => apiPost('/wiki/admin/retrain', {}, h()),
            adminUsers: () => apiFetch('/wiki/admin/users', h()),
            getUserAcl: (uid) => apiFetch(`/wiki/admin/acl/user/${uid}`, h()),
            setUserAcl: (uid, body) => apiPut(`/wiki/admin/acl/user/${uid}`, body, h()),
        };
    }, [getAuthHeader]);
}
